import { request, type ApiResult } from '@/utils/request'
import type { PageVO, CornerUserVO } from './corner'

// ==================== DTO / VO 类型 ====================

export interface ProfileUpdateDTO {
  userName?: string
  avatar?: string
  avatarHd?: string       // 高清头像URL（1080px，全屏预览；与 avatar 成对提交）
  realName?: string       // 真实姓名（可编辑，非实名提交专用）
  gender?: number
  birthday?: string      // YYYY-MM-DD
  stature?: number
  weight?: number
  homeProvince?: string
  homeCity?: string
  livingProvince?: string
  livingCity?: string
  marriage?: number | string
  education?: number | string
  educationImages?: string  // 学历证明图片URL列表（逗号分隔，最多3张）
  job?: number | string
  jobImages?: string  // 工作证明图片URL列表（逗号分隔，最多3张）
  salary?: number | string
  school?: string
  signature?: string
  backImage?: string
  privacy?: number
}

export interface PasswordUpdateDTO {
  oldPassword: string
  newPassword: string
}

/**
 * 实名认证提交（姓名 + 身份证号）
 * 认证中心 与 功能拦截弹窗共用 POST /user/real-name/submit
 */
export interface RealNameSubmitDTO {
  realName: string
  /** 首次认证必填；身份证号一经填写即不可变更，再次提交可留空 */
  idCard?: string
}

/**
 * 8 个择偶偏好字段（与 /match/preference 对齐）
 */
export interface MatchPreferenceDTO {
  partnerAgeFrom?: number
  partnerAgeTo?: number
  partnerStatureFrom?: number
  partnerStatureTo?: number
  partnerMinEducation?: number
  partnerMinSalary?: number
  partnerMarriagePref?: number[]
  partnerCity?: string
}

export interface UserCardVO {
  id: number
  userId?: number
  oid?: string
  displayUid?: string
  userName?: string
  avatar?: string
  /** 高清头像URL（1080px，本人/用户详情全屏预览；历史为空回退 avatar） */
  avatarHd?: string
  gender?: number
  birthday?: string
  age?: number
  constellation?: string
  stature?: number
  weight?: number
  education?: number | string       // 字典ID (后端返回 Integer)
  educationName?: string            // 字典名
  educationImages?: string  // 学历证明图片URL列表（逗号分隔，最多3张）
  educationStatus?: number  // 学历认证状态 0待审 1通过 2驳回 -1未提交
  job?: number | string             // 字典ID (后端返回 Integer)
  jobName?: string                  // 字典名
  jobImages?: string  // 工作证明图片URL列表（逗号分隔，最多3张）
  jobStatus?: number  // 工作认证状态 0待审 1通过 2驳回 -1未提交
  salary?: number | string          // 字典ID (后端返回 Integer)
  salaryName?: string               // 字典名
  marriage?: number | string        // 字典ID (后端返回 Integer)
  marriageName?: string             // 字典名
  home?: string
  living?: string
  school?: string
  signature?: string
  vipLevel?: number
  vipStatus?: number
  isVerified?: boolean
  infoScore?: number
  createTime?: string
  /** 真实姓名（本人资料回显） */
  realName?: string
  /** 身份证号脱敏回显（前4后4，中间掩码） */
  idCardMask?: string
  /** 实名认证状态 0未 1通过 2驳回 */
  realNameStatus?: number
  /** 增强实名开关 1开启（聊天/发轨迹/回复评论/打招呼需实名）0关闭则不弹实名窗 */
  enhanceRealNameEnabled?: number
  /** MBTI 类型缩写（INTJ 等），未测过为 null */
  mbtiType?: string | null
  /** 个人形象图片URL列表（已过审，按 sort 排序） */
  photos?: string[]
  /** 自我介绍条目（0关于我 1兴趣爱好 2感情观 3心仪的TA） */
  introItems?: string[]
}

// ==================== 接口方法 ====================

/**
 * 获取当前登录用户信息
 * GET /user/me
 */
export function getMe(): Promise<ApiResult<UserCardVO>> {
  return request.get<UserCardVO>('/user/me')
}

/**
 * 更新用户档案
 * PUT /user/profile（若后端是 POST，底层 request 会按后端路由兼容）
 */
export function updateProfile(data: ProfileUpdateDTO): Promise<ApiResult<boolean>> {
  return request.put<boolean>('/user/profile', data)
}

/**
 * 提交实名认证（姓名+身份证号）
 * POST /user/real-name/submit，认证中心与实名拦截弹窗共用
 * 成功后返回最新资料（含 realNameStatus / idCardMask），全局实名状态即时生效
 */
export function submitRealName(data: RealNameSubmitDTO): Promise<ApiResult<UserCardVO>> {
  return request.post<UserCardVO>('/user/real-name/submit', data)
}

/**
 * 修改密码
 * PUT /user/password
 */
export function updatePassword(data: PasswordUpdateDTO): Promise<ApiResult<boolean>> {
  return request.put<boolean>('/user/password', data)
}

/**
 * 设置择偶偏好（8 个 partner 字段）
 * POST /match/preference
 */
export function setPreference(data: MatchPreferenceDTO): Promise<ApiResult<boolean>> {
  return request.post<boolean>('/match/preference', data)
}

/**
 * 头像上传结果（2026-09-27 双版本）
 * · avatar：200px 缩略图（列表/消息流）
 * · avatarHd：1080px 高清图（全屏预览；gif/webp 等格式与 avatar 相同）
 */
export interface AvatarUploadResult {
  url?: string
  avatar: string
  avatarHd: string
  [key: string]: unknown
}

/**
 * 上传头像：multipart/form-data
 * POST /user/avatar/upload；后端一次生成「缩略图 + 高清图」并返回双 URL
 */
export async function uploadAvatar(formData: FormData): Promise<ApiResult<AvatarUploadResult>> {
  return request.post<AvatarUploadResult>('/user/avatar/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

/**
 * 上传学历/工作证明图片：multipart/form-data
 * POST /user/cert/upload（登录态鉴权；10MB 上限；自动压缩+清理EXIF；返回 URL 字符串）
 * 调用方拿到 url 后随 /user/profile 一起保存，后端会自动重置对应认证状态为待审
 */
export function uploadCertImage(formData: FormData): Promise<ApiResult<string>> {
  return request.post<string>('/user/cert/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

/**
 * 喜欢列表查询 DTO
 */
export interface LikeListQueryDTO {
  pageNum?: number
  pageSize?: number
  /** 搜索关键词（UID/昵称） */
  keyword?: string
}

/**
 * 我喜欢的用户列表（静默模式：无数据时不弹 toast）
 * GET /user/likes/my
 */
export function listMyLikes(params?: LikeListQueryDTO): Promise<ApiResult<PageVO<CornerUserVO>>> {
  return request.getSilent<PageVO<CornerUserVO>>('/user/likes/my', params)
}

/**
 * 喜欢我的用户列表（静默模式：无数据时不弹 toast）
 * GET /user/likes/liked-me
 */
export function listLikedMe(params?: LikeListQueryDTO): Promise<ApiResult<PageVO<CornerUserVO>>> {
  return request.getSilent<PageVO<CornerUserVO>>('/user/likes/liked-me', params)
}

/** 喜欢 / 取消喜欢结果 VO（后端 LikeResultVO） */
export interface LikeResultVO {
  /** 操作后是否已喜欢：1已喜欢 0已取消 */
  likedFlag: number
  /** 对方当前被喜欢总数 */
  likeCount: number
}

/**
 * 喜欢用户（幂等，支持取消后重新喜欢）
 * POST /user/likes/{userId}
 */
export function likeUser(userId: number | string): Promise<ApiResult<LikeResultVO>> {
  return request.post<LikeResultVO>(`/user/likes/${userId}`)
}

/**
 * 取消喜欢（幂等）
 * DELETE /user/likes/{userId}
 */
export function unlikeUser(userId: number | string): Promise<ApiResult<LikeResultVO>> {
  return request.delete<LikeResultVO>(`/user/likes/${userId}`)
}

/** 个人中心统计 VO（后端 /user/stats 返回） */
export interface UserStatsVO {
  followCount?: number       // 被关注总数
  followingCount?: number    // 我关注的总数
  activitiesCount?: number   // 报名活动数
  dynamicsCount?: number     // 发布动态数
  viewCount?: number         // 被浏览总数（lf_guest_visit 聚合）
  visitorCount?: number      // 去重访客数（多少人看过）
}

/**
 * 个人中心统计（关注/活动/动态数聚合）
 * GET /user/stats（登录态）
 */
export function getStats(): Promise<ApiResult<UserStatsVO>> {
  return request.get<UserStatsVO>('/user/stats')
}
